import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useKeenSlider } from 'keen-slider/react';
import { useRouter } from 'next/router';
import Button from 'src/components/Button';
import ModalLayoutBase, { MobileModalType, ModalContent as ModalContentBase, ModalHeader, ModalHeaderTitle } from 'src/components/ModalLayout';
import { NoticePopup, NoticePopupButton } from 'src/hooks/usePopupQuery/types';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { setTargetBlankToLinks } from 'src/utils/html';
const ModalLayout = styled(ModalLayoutBase)`
  max-width: 500px;
`;
const ModalContent = styled(ModalContentBase)`
  padding: 0px;
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  width: 100%;
`;
const ContentTitle = styled.span`
  color: ${({
  theme
}) => theme.color.contentDeep};
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 8px;
  ${({
  theme
}) => theme.screenSize.mobile} {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
  }
`;
const ContentDescription = styled.span`
  color: ${({
  theme
}) => theme.color.content9};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  a {
    color: inherit;
    font-weight: 700;
  }
  ${({
  theme
}) => theme.screenSize.mobile} {
    text-align: center;
  }
`;
const ContentImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  margin-bottom: 24px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    padding: 32px 24px 0px 24px;
    margin-bottom: 16px;
  }
`;
const ContentImage = styled.img`
  width: 100%;
  height: 100%;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    border-radius: 12px;
  }
`;
const ContentInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
`;
const NoticePopupButtonWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-start;
  gap: 8px;
  padding: 16px 24px 24px 24px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    flex-direction: column;
    gap: 16px;
  }
`;
const StyledNoticePopupButton = styled(Button)<{
  backgroundColor: string;
  buttonTextColor: string;
  darkModeBackgroundColor: string;
  darkModeButtonTextColor: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 95px;
  width: auto;

  &,
  &:disabled,
  &:active,
  &:hover {
    background-color: ${({
  backgroundColor,
  darkModeBackgroundColor,
  theme
}) => theme.isDark ? darkModeBackgroundColor : backgroundColor};
    color: ${({
  buttonTextColor,
  darkModeButtonTextColor,
  theme
}) => theme.isDark ? darkModeButtonTextColor : buttonTextColor};
  }
`;
const SlideWrapper = styled.div`
  align-content: flex-start;
  display: flex;
  overflow: hidden;
  position: relative;
  touch-action: pan-y;
  user-select: none;
  width: 100%;
`;
const CloseButton = styled(Button)`
  padding-left: 16px;
  padding-right: 16px;

  width: ${({
  variant
}) => variant === 'text' ? 'fit-content' : 'auto'};
  min-width: ${({
  variant
}) => variant === 'text' ? 'unset' : '95px'};
  ${({
  theme
}) => theme.screenSize.mobile} {
    width: 100%;
  }
`;
interface Props {
  popup: NoticePopup;
  onDisplay: () => void;
}
const NoticePopupModal = ({
  popup,
  onDisplay
}: Props) => {
  const closeModal = useSetAtom(closeModalAtom);
  const router = useRouter();
  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: true
  });
  useEffect(() => {
    onDisplay();
  }, [onDisplay]);
  const handleButtonClick = (button: NoticePopupButton) => {
    // back office 상에서는 type이 LINK인 경우 webLink는 required
    if (!button.webLink) {
      closeModal(ModalType.NOTICE_POPUP);
      return;
    }
    if (button.webLink?.startsWith('/')) {
      router.push(button.webLink as '/');
    } else {
      window.open(button.webLink, '_blank');
    }
  };
  const handleClose = () => {
    closeModal(ModalType.NOTICE_POPUP);
  };
  const {
    buttons
  } = popup;
  return <ModalLayout isDim closeWhenClickOutside={true} mobileType={MobileModalType.BOTTOM_SHEET} data-testid='Notice-Popup' navButtonType={ModalNavButtonType.NONE} onClose={handleClose} data-sentry-element="ModalLayout" data-sentry-component="NoticePopupModal" data-sentry-source-file="index.tsx">
      {popup.title && <ModalHeader>
          <ModalHeaderTitle mobileType={MobileModalType.BOTTOM_SHEET}>
            {popup.title.headTitle}
          </ModalHeaderTitle>
        </ModalHeader>}
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <SlideWrapper ref={sliderRef} data-sentry-element="SlideWrapper" data-sentry-source-file="index.tsx">
          {popup.contents.map(content => <ContentWrapper key={content.id}>
              {content.image && <ContentImageWrapper>
                  <ContentImage src={content.image.url} alt={content.title || ''} width={content.image.width} height={content.image.height} />
                </ContentImageWrapper>}
              <ContentInfo>
                {content.title && <ContentTitle>{content.title}</ContentTitle>}
                {content.content && <ContentDescription dangerouslySetInnerHTML={{
              __html: setTargetBlankToLinks(content.content)
            }} />}
              </ContentInfo>
            </ContentWrapper>)}
        </SlideWrapper>
        <NoticePopupButtonWrapper data-sentry-element="NoticePopupButtonWrapper" data-sentry-source-file="index.tsx">
          {buttons.map(button => <StyledNoticePopupButton id={button.label} key={button.label} backgroundColor={button.backgroundColor} darkModeBackgroundColor={button.darkModeBackgroundColor} buttonTextColor={button.color} darkModeButtonTextColor={button.darkModeColor} onClick={() => handleButtonClick(button)}>
              {button.label}
            </StyledNoticePopupButton>)}
          {popup.closeButtonText.length > 0 && <CloseButton id='notice-popup-close-button' variant={buttons.length > 0 ? 'text' : 'contained'} onClick={handleClose}>
              {popup.closeButtonText}
            </CloseButton>}
        </NoticePopupButtonWrapper>
      </ModalContent>
    </ModalLayout>;
};
export default NoticePopupModal;